import type {
  GradingAnswer,
  GradingCalibrationAnswer,
  GradingCalibrationQuestion,
  GradingDiffFactor,
  GradingFamily,
  GradingQuestion,
  GradingRole,
  GradingRoleCluster,
  GradingSpecialization,
  GradingTreePayload,
} from './types';

export function resolveRolePath(
  role: GradingRole,
  specs: GradingSpecialization[],
  roleClusters: GradingRoleCluster[],
  families: GradingFamily[],
) {
  const roleCluster = roleClusters.find((s) => Number(s.id) === Number(role.role_cluster_id));
  const family = roleCluster ? families.find((f) => Number(f.id) === Number(roleCluster.family_id)) : undefined;
  const spec = role.specialization_id != null
    ? specs.find((s) => Number(s.id) === Number(role.specialization_id))
    : undefined;
  return { family, roleCluster, spec, role };
}

export function compareDiffFactorOrder(
  a: Pick<GradingDiffFactor, 'sort_order' | 'id'>,
  b: Pick<GradingDiffFactor, 'sort_order' | 'id'>,
): number {
  const orderA = a.sort_order ?? 0;
  const orderB = b.sort_order ?? 0;
  if (orderA !== orderB) return orderA - orderB;
  return a.id - b.id;
}

export function sortDiffFactorsForFamily(
  factors: GradingDiffFactor[],
  familyId: number,
): GradingDiffFactor[] {
  return factors
    .filter((factor) => factor.family_id === familyId)
    .sort(compareDiffFactorOrder);
}

function gradingNum(value: unknown): number {
  return typeof value === 'number' ? value : Number(value);
}

function gradingNumOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = gradingNum(value);
  return Number.isNaN(n) ? null : n;
}

export function formatGradingScore(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

export function parseGradingScoreInput(value: string): number | null {
  const normalized = value.replace(/\s/g, '').replace(',', '.');
  if (!normalized.trim()) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

export function normalizeGradingTreePayload(data: GradingTreePayload): GradingTreePayload {
  return {
    families: (data.families || []).map((f) => ({ ...f, id: gradingNum(f.id) })),
    roleClusters: (data.roleClusters || []).map((s) => ({
      ...s,
      id: gradingNum(s.id),
      family_id: gradingNum(s.family_id),
    })),
    specializations: (data.specializations || []).map((s) => ({
      ...s,
      id: gradingNum(s.id),
      family_id: gradingNum(s.family_id),
    })),
    roles: (data.roles || []).map((r) => ({
      ...r,
      id: gradingNum(r.id),
      role_cluster_id: gradingNum(r.role_cluster_id),
      specialization_id: gradingNumOrNull(r.specialization_id),
      grade_level: gradingNum(r.grade_level),
      min_score: gradingNumOrNull(r.min_score),
      max_score: gradingNumOrNull(r.max_score),
    })),
    factors: (data.factors || []).map((f) => ({
      ...f,
      id: gradingNum(f.id),
      family_id: gradingNum(f.family_id),
      sort_order: gradingNum(f.sort_order ?? 0),
    })),
    questions: (data.questions || []).map((q) => ({
      ...q,
      id: gradingNum(q.id),
      diff_factor_id: gradingNum(q.diff_factor_id),
      role_cluster_id: gradingNum(q.role_cluster_id),
    })),
    answers: (data.answers || []).map((a) => ({
      ...a,
      id: gradingNum(a.id),
      question_id: gradingNum(a.question_id),
      answer_score: gradingNum(a.answer_score),
    })),
    calibrationQuestions: (data.calibrationQuestions || []).map((q) => ({
      ...q,
      id: gradingNum(q.id),
      family_id: gradingNum(q.family_id),
    })),
    calibrationAnswers: (data.calibrationAnswers || []).map((a) => ({
      ...a,
      id: gradingNum(a.id),
      calibration_question_id: gradingNum(a.calibration_question_id),
      target_role_cluster_id: gradingNum(a.target_role_cluster_id),
      sort_order: gradingNum(a.sort_order),
    })),
  };
}

export function countQuestionsForFamily(
  familyId: number,
  roleClusters: GradingRoleCluster[],
  factors: GradingDiffFactor[],
  questions: GradingQuestion[],
) {
  const clusterIds = roleClusters.filter((s) => s.family_id === familyId).map((s) => s.id);
  const factorIds = factors.filter((f) => f.family_id === familyId).map((f) => f.id);
  return questions.filter((q) => factorIds.includes(q.diff_factor_id) && clusterIds.includes(q.role_cluster_id)).length;
}

export function countQuestionsForRoleCluster(
  roleClusterId: number,
  familyId: number,
  factors: GradingDiffFactor[],
  questions: GradingQuestion[],
) {
  const factorIds = factors.filter((f) => f.family_id === familyId).map((f) => f.id);
  return questions.filter((q) => q.role_cluster_id === roleClusterId && factorIds.includes(q.diff_factor_id)).length;
}

export function countRolesForFamily(familyId: number, roleClusters: GradingRoleCluster[], roles: GradingRole[]) {
  const clusterIds = roleClusters.filter((s) => s.family_id === familyId).map((s) => s.id);
  return roles.filter((r) => clusterIds.includes(r.role_cluster_id)).length;
}

export function questionScoreRange(answers: GradingAnswer[], questionId: number) {
  const scores = answers.filter((a) => a.question_id === questionId).map((a) => a.answer_score);
  if (!scores.length) return { min: null, max: null };
  return { min: Math.min(...scores), max: Math.max(...scores) };
}

export type DiagnosticItem = { level: 'warn' | 'error'; text: string };

export function runGradingDiagnostics(input: {
  families: GradingFamily[];
  roleClusters: GradingRoleCluster[];
  roles: GradingRole[];
  factors: GradingDiffFactor[];
  questions: GradingQuestion[];
  answers: GradingAnswer[];
  calibrationQuestions: GradingCalibrationQuestion[];
  calibrationAnswers: GradingCalibrationAnswer[];
}): DiagnosticItem[] {
  const items: DiagnosticItem[] = [];
  const { families, roleClusters, roles, factors, questions, answers, calibrationQuestions, calibrationAnswers } = input;

  families.forEach((f) => {
    if (!roleClusters.some((s) => s.family_id === f.id)) {
      items.push({ level: 'error', text: `Family «${f.name}»: нет role cluster` });
    }
    const calib = calibrationQuestions.find((q) => q.family_id === f.id);
    if (!calib) items.push({ level: 'warn', text: `Family «${f.name}»: нет калибровочного вопроса` });
    else if (!calibrationAnswers.some((a) => a.calibration_question_id === calib.id)) {
      items.push({ level: 'error', text: `Family «${f.name}»: калибровка без ответов` });
    }
  });

  roleClusters.forEach((rc) => {
    if (!roles.some((r) => r.role_cluster_id === rc.id)) {
      items.push({ level: 'warn', text: `Role cluster «${rc.name}»: нет Role` });
    }
  });

  roles.forEach((r) => {
    if (r.min_score == null || r.max_score == null) {
      items.push({ level: 'warn', text: `Role «${r.name}»: не задан диапазон баллов` });
    }
  });

  const byCluster = new Map<number, GradingRole[]>();
  roles.forEach((r) => {
    if (r.min_score == null || r.max_score == null) return;
    const list = byCluster.get(r.role_cluster_id) || [];
    list.push(r);
    byCluster.set(r.role_cluster_id, list);
  });
  byCluster.forEach((list) => {
    for (let i = 0; i < list.length; i++) {
      for (let j = i + 1; j < list.length; j++) {
        const a = list[i];
        const b = list[j];
        if (a.min_score! <= b.max_score! && b.min_score! <= a.max_score!) {
          items.push({ level: 'error', text: `Пересечение баллов: «${a.name}» и «${b.name}»` });
        }
      }
    }
  });

  questions.forEach((q) => {
    if (!answers.some((a) => a.question_id === q.id)) {
      items.push({ level: 'error', text: `Вопрос «${q.question_text.slice(0, 40)}…»: нет ответов` });
    }
  });

  if (!factors.length && families.length) {
    items.push({ level: 'warn', text: 'Нет дифференцирующих факторов' });
  }

  return items;
}

export function formatPath(parts: (string | undefined | null)[]) {
  return parts.filter(Boolean).join(' → ');
}

export function getCalibrationForFamily(
  familyId: number,
  calibrationQuestions: GradingCalibrationQuestion[],
  calibrationAnswers: GradingCalibrationAnswer[],
) {
  const question = calibrationQuestions.find((q) => q.family_id === familyId);
  if (!question) return { question: null, answers: [] as GradingCalibrationAnswer[] };
  return {
    question,
    answers: calibrationAnswers
      .filter((a) => a.calibration_question_id === question.id)
      .sort((a, b) => a.sort_order - b.sort_order),
  };
}

export async function confirmAndRunDelete(
  label: string,
  message: string,
  action: () => Promise<void>,
  reload: () => Promise<void>,
) {
  if (!window.confirm(`${message}\n\nУдалить «${label}»?`)) return;
  try {
    await action();
    await reload();
  } catch (e) {
    window.alert(e instanceof Error ? e.message : 'Ошибка удаления');
  }
}
